import React from 'react'
import { UserMessage } from '../UserMessage/UserMessage'
import { AssistantMessage } from '../AssistantMessage/AssistantMessage'
import { StructuredMessage } from '../StructuredMessage/StructuredMessage'
import { formatChatRequest } from '../../handlers/chatMessageHandlers'
import { EVENTS, trackEvent } from '../../../../../events'

export const ChatMessage = ({
    message,
    selectedInbox,
    currentChat,
    analysisMode,
    sendMessage,
    addMessage,
    setAiStatus,
    inputMessage,
    showProcessingSteps,
    showQueryDetails,
    lastFollowUpPrompts,
    isPublic,
    isProcessing,
    canSendPrompt,
    getLimitMessage,
    handleSendMessage,
    isEmbedded = false,
    handleExport,
    isLastVisualization = false,
    setSelectedTab,
    mlConfig,
    setMlConfig,
    setMLResults,
    submitFeedback,
    isSubmittingFeedback,
    isLastMessage,
    isCurrentSession,
    isTourWorkspace,
}) => {
    // Handle follow-up prompt sending
    const handleSendFollowUp = (promptText) => {
        // Don't send if already processing
        if (isProcessing) return

        // Check prompt limit for demo mode
        if (canSendPrompt && !canSendPrompt()) {
            if (getLimitMessage) {
                const limitMessage = getLimitMessage()
                if (limitMessage && addMessage && currentChat) {
                    addMessage({
                        messageID: Date.now(),
                        text: limitMessage,
                        role: 'system',
                        timestamp: new Date().toISOString(),
                    })
                }
            }
            return
        }

        trackEvent(EVENTS.SCOOP_AI.CLICK_FOLLOWUP, { prompt: promptText })
        // Use the unified handleSendMessage function
        handleSendMessage(promptText)
    }

    // Handle quick/deep analysis selection
    const handleQuickAnalysis = (originalQuery) => {
        // Use the original query from the NEEDS_REASONING response
        const queryToUse = originalQuery || inputMessage

        if (!queryToUse || queryToUse.trim() === '') {
            console.error('No query available for quick analysis')
            return
        }

        // Auth data will be added by useWebSocket
        const request = formatChatRequest(queryToUse, selectedInbox?.inboxID, currentChat.chatID, {
            forceReasoning: false,
            checkReasoning: false, // Don't check again
        })
        sendMessage(request)
        setAiStatus({ type: 'processing', message: 'Running quick analysis...' })
    }

    const handleDeepAnalysis = (originalQuery) => {
        // Use the original query from the NEEDS_REASONING response
        const queryToUse = originalQuery || inputMessage

        if (!queryToUse || queryToUse.trim() === '') {
            console.error('No query available for deep analysis')
            return
        }

        // Auth data will be added by useWebSocket
        const request = formatChatRequest(queryToUse, selectedInbox?.inboxID, currentChat.chatID, {
            forceReasoning: true,
            checkReasoning: false, // Don't check (we already know)
        })
        sendMessage(request)
        setAiStatus({
            type: 'processing',
            message: 'Starting deep analysis... This may take a minute.',
        })
    }

    // Render appropriate message component based on type
    if (message.role === 'user') {
        return <UserMessage message={message} />
    }

    // Error or system messages - use AssistantMessage for simple display
    if (message.role === 'error' || message.role === 'system') {
        return (
            <AssistantMessage
                message={message}
                followUpPrompts={lastFollowUpPrompts}
                onSendFollowUp={handleSendFollowUp}
            />
        )
    }

    // Structured API response or dataset description
    if (
        (message.type === 'structured_response' && message.header) ||
        message.type === 'dataset_description'
    ) {
        return (
            <StructuredMessage
                message={message}
                onSendFollowUp={handleSendFollowUp}
                analysisMode={analysisMode}
                showProcessingSteps={showProcessingSteps}
                showQueryDetails={showQueryDetails}
                sendMessage={sendMessage}
                isPublic={isPublic}
                isProcessing={isProcessing}
                isEmbedded={isEmbedded}
                handleExport={handleExport}
                isLastVisualization={isLastVisualization}
                selectedInbox={selectedInbox}
                setSelectedTab={setSelectedTab}
                mlConfig={mlConfig}
                setMlConfig={setMlConfig}
                setMLResults={setMLResults}
                submitFeedback={submitFeedback}
                isSubmittingFeedback={isSubmittingFeedback}
                isLastMessage={isLastMessage}
                isCurrentSession={isCurrentSession}
                isTourWorkspace={isTourWorkspace}
            />
        )
    }

    // Regular assistant message (including needs reasoning)
    return (
        <AssistantMessage
            message={message}
            onQuickAnalysis={handleQuickAnalysis}
            onDeepAnalysis={handleDeepAnalysis}
            onSendFollowUp={handleSendFollowUp}
        />
    )
}
